import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

import { parseSearchParams } from '@/utils/stringUtils';
import {
  isValidUuidV4,
  generateSessionId,
  SH_SPRING_ID,
  SH_PRODUCT,
  SH_SESSION_START,
} from '@/utils/springHealthUtils';
import { useSpringId } from '@/hooks/useSpringId';

// Renderless component that extracts spring_id from URL query params,
// validates UUID v4 format, persists to sessionStorage, generates a session_id,
// registers both as PostHog super properties, and stores in SpringIdContext.
// On page refresh, hydrates from sessionStorage (via SpringIdProvider).
// Follows the OrgSlugCapture pattern.

// Matches /activate/spring-health/{product} or /activate/{product}/...
const extractProductFromPath = (pathname: string): string | undefined => {
  const match = pathname.match(/\/activate\/(?:spring-health\/)?([^/]+)/);
  return match?.[1];
};

const SpringIdCapture: React.FC = () => {
  const location = useLocation();
  const posthog = usePostHog();
  const {
    springId, sessionId, setSpringId, setSessionId,
  } = useSpringId();

  useEffect(() => {
    const searchParams = parseSearchParams(location.search);
    const springIdParam = searchParams.spring_id;

    if (springIdParam && isValidUuidV4(springIdParam)) {
      const storedSpringId = sessionStorage.getItem(SH_SPRING_ID);
      const isNewSession = springIdParam !== storedSpringId;

      setSpringId(springIdParam);

      // Generate new session_id for new sessions or if not already set
      if (isNewSession || !sessionId) {
        const newSessionId = generateSessionId();
        setSessionId(newSessionId);
      }

      // Persist product and session start time
      const product = extractProductFromPath(location.pathname);
      if (product) {
        sessionStorage.setItem(SH_PRODUCT, product);
      }
      // Reset session start time for new sessions; keep existing on page refresh
      if (isNewSession || !sessionStorage.getItem(SH_SESSION_START)) {
        sessionStorage.setItem(SH_SESSION_START, String(Date.now()));
      }
    }
  }, [location.search, location.pathname, setSpringId, sessionId, setSessionId]);

  // Register PostHog super properties when values are available
  useEffect(() => {
    if (posthog && springId) {
      const properties: Record<string, string> = { spring_id: springId };
      if (sessionId) {
        properties.spring_session_id = sessionId;
      }
      posthog.register(properties);
    }
  }, [posthog, springId, sessionId]);

  return null;
};

export default SpringIdCapture;
